import { useState, useEffect } from "react";
import { C, getFontFamily } from "../constants/colors";
import { getDashboardStats, getRevisitSuggestions } from "../progress";

export function Dashboard({ profile, lang, onBack, onChapter, curriculumData }) {
  const ff = getFontFamily(lang);
  const isHi = lang === "hi";
  const currentClass = profile?.studentClass || 10;
  const [stats, setStats] = useState(null);
  const [expandedSubject, setExpandedSubject] = useState(null);

  useEffect(() => {
    setStats(getDashboardStats(currentClass));
  }, [currentClass]);

  if (!stats) return null;

  // Get total chapters from curriculum for this class
  const getTotalChapters = (subject) => {
    if (!curriculumData?.CBSE?.[String(currentClass)]?.[subject]) return 0;
    return curriculumData.CBSE[String(currentClass)][subject].chapters?.length || 0;
  };

  const allSubjects = curriculumData?.CBSE?.[String(currentClass)]
    ? Object.keys(curriculumData.CBSE[String(currentClass)])
    : [];

  const totalChaptersAll = allSubjects.reduce((sum, s) => sum + getTotalChapters(s), 0);

  const subjectColors = {
    Maths: { bg: "#FAEEDA", color: "#854F0B", bar: "#EF9F27", ring: "#EF9F27" },
    Science: { bg: "#E8F2F8", color: "#085041", bar: "#0F6E56", ring: "#0F6E56" },
    Geography: { bg: "#EAF3DE", color: "#27500A", bar: "#3B6D11", ring: "#3B6D11" },
    Economics: { bg: "#EEEDFE", color: "#3C3489", bar: "#534AB7", ring: "#534AB7" },
  };

  const encouragement = stats.chaptersCompleted === 0
    ? (isHi ? "पहला कदम सबसे ज़रूरी है — शुरू करो!" : "The first step matters most — start exploring!")
    : stats.chaptersCompleted < 5
    ? (isHi ? "अच्छी शुरुआत! ऐसे ही आगे बढ़ो।" : "Great start! Keep going.")
    : (isHi ? "शानदार! तुम बहुत अच्छा कर रहे हो।" : "You're doing really well!");

  // Ring chart SVG helper
  const Ring = ({ value, total, color, size = 80, label, sublabel }) => {
    const r = (size / 2) - 8;
    const circ = 2 * Math.PI * r;
    const pct = total > 0 ? value / total : 0;
    const offset = circ * (1 - pct);
    return (
      <div style={{ textAlign: "center" }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#EDF4F8" strokeWidth="6"/>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={circ} strokeDashoffset={offset}
            transform={`rotate(-90 ${size/2} ${size/2})`} strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 0.8s ease" }}/>
          <text x={size/2} y={size/2 - 4} textAnchor="middle" fontSize="18" fontWeight="600" fill={color}>{value}</text>
          <text x={size/2} y={size/2 + 10} textAnchor="middle" fontSize="9" fill="#94a3b8">{sublabel}</text>
        </svg>
        <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>{label}</div>
      </div>
    );
  };

  // Get chapter completion status for a subject
  const getChapterStatuses = (subject) => {
    if (!curriculumData?.CBSE?.[String(currentClass)]?.[subject]) return [];
    const chapters = curriculumData.CBSE[String(currentClass)][subject].chapters || [];
    return chapters.map(ch => {
      const key = `${currentClass}-${subject}-${ch.name}`;
      const progress = stats.allChapters?.[key];
      return {
        name: ch.name,
        ncert: ch.ncert_chapter,
        conceptCount: ch.concepts?.length || 0,
        status: progress?.completed ? "completed" : progress ? "in-progress" : "not-started",
      };
    });
  };

  return (
    <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#E8F2F8", fontFamily: ff }}>
      {/* Header */}
      <div style={{ padding: "16px 24px", display: "flex", alignItems: "center", gap: 12 }}>
        <div onClick={onBack} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12l-4-4 4-4" stroke="#0f172a" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{isHi ? "वापस" : "Back"}</span>
        </div>
      </div>

      <div style={{ padding: "0 24px 32px", maxWidth: 460, margin: "0 auto" }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 600, color: C.text, marginBottom: 4 }}>
            {isHi ? "आपकी यात्रा" : "Your journey"}
            <span style={{ fontSize: 13, fontWeight: 500, color: "#64748b", marginLeft: 8 }}>
              {isHi ? `कक्षा ${currentClass}` : `Class ${currentClass}`}
            </span>
          </div>
          <div style={{ fontSize: 13, color: "#64748b" }}>{encouragement}</div>
        </div>

        {/* Ring charts */}
        <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 24 }}>
          <Ring value={stats.chaptersCompleted} total={totalChaptersAll} color="#0F6E56"
            label={isHi ? "पूरे किये" : "Completed"} sublabel={`of ${totalChaptersAll}`} />
          <Ring value={stats.daysActive} total={30} color="#EF9F27"
            label={isHi ? "सक्रिय दिन" : "Days active"} sublabel="this month" />
          <Ring value={stats.chaptersVisited} total={totalChaptersAll} color="#534AB7"
            label={isHi ? "Explore किये" : "Explored"} sublabel={`of ${totalChaptersAll}`} />
        </div>

        {/* Week activity */}
        <div style={{ background: "white", borderRadius: 16, padding: "14px 18px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 10 }}>
            {isHi ? "इस हफ्ते" : "This week"}
          </div>
          <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
            {["M", "T", "W", "T", "F", "S", "S"].map((day, i) => {
              const today = new Date();
              const dayOfWeek = (today.getDay() + 6) % 7; // Monday = 0
              const date = new Date(today);
              date.setDate(today.getDate() - dayOfWeek + i);
              const dateStr = date.toISOString().slice(0, 10);
              const isActive = stats.activeDaysList?.includes(dateStr);
              const isToday = i === dayOfWeek;
              return (
                <div key={i} style={{ width: 32, textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: "#94a3b8", marginBottom: 4 }}>{day}</div>
                  <div style={{
                    width: 32, height: 32, borderRadius: 10,
                    background: isActive ? "#0F6E56" : isToday ? "#E8F2F8" : "#EDF4F8",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: isToday && !isActive ? "1.5px solid #0F6E56" : "none",
                  }}>
                    {isActive && <span style={{ color: "white", fontSize: 10 }}>✓</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Subject chapters — expandable */}
        {allSubjects.map(subject => {
          const sc = subjectColors[subject] || subjectColors.Science;
          const chapters = getChapterStatuses(subject);
          const completed = chapters.filter(c => c.status === "completed").length;
          const inProgress = chapters.filter(c => c.status === "in-progress").length;
          const isExpanded = expandedSubject === subject;

          return (
            <div key={subject} style={{ background: "white", borderRadius: 18, padding: "16px 18px", marginBottom: 12 }}>
              <div onClick={() => setExpandedSubject(isExpanded ? null : subject)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 5, background: sc.bar }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{subject}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>
                    {completed}/{chapters.length}
                  </span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                    style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0)", transition: "transform .2s" }}>
                    <path d="M4 2l4 4-4 4" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
              </div>

              {/* Progress bar */}
              <div style={{ height: 6, background: "#EDF4F8", borderRadius: 3, marginTop: 10, overflow: "hidden", display: "flex" }}>
                {completed > 0 && <div style={{ width: `${(completed / chapters.length) * 100}%`, background: sc.bar, borderRadius: "3px 0 0 3px" }} />}
                {inProgress > 0 && <div style={{ width: `${(inProgress / chapters.length) * 100}%`, background: `${sc.bar}55` }} />}
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                {completed > 0 && <span style={{ fontSize: 9, color: sc.color }}>● {completed} done</span>}
                {inProgress > 0 && <span style={{ fontSize: 9, color: "#BA7517" }}>● {inProgress} in progress</span>}
                {chapters.length - completed - inProgress > 0 && <span style={{ fontSize: 9, color: "#94a3b8" }}>● {chapters.length - completed - inProgress} remaining</span>}
              </div>

              {/* Expanded chapter list */}
              {isExpanded && (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                  {chapters.map((ch, i) => (
                    <div key={i} onClick={() => onChapter?.(subject, ch.name)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                        background: ch.status === "completed" ? "#f0fdf4" : ch.status === "in-progress" ? "#FAEEDA" : "#f8fafc",
                        borderRadius: 12, cursor: "pointer",
                      }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        background: ch.status === "completed" ? "#16a34a" : ch.status === "in-progress" ? "#EF9F27" : "#e2e8f0",
                      }}>
                        {ch.status === "completed" ? <span style={{ color: "white", fontSize: 10 }}>✓</span>
                          : ch.status === "in-progress" ? <span style={{ color: "white", fontSize: 10 }}>◐</span>
                          : <span style={{ color: "#94a3b8", fontSize: 10 }}>○</span>}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: ch.status === "not-started" ? "#94a3b8" : C.text }}>{ch.name}</div>
                        <div style={{ fontSize: 10, color: "#94a3b8" }}>{ch.conceptCount} concepts</div>
                      </div>
                      {ch.status === "in-progress" && <span style={{ fontSize: 10, color: "#BA7517", fontWeight: 500 }}>{isHi ? "जारी →" : "Continue →"}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Empty state */}
        {stats.chaptersVisited === 0 && (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📚</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              {isHi ? "अभी तक कुछ explore नहीं किया" : "Nothing explored yet"}
            </div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
              {isHi ? "एक अध्याय से शुरू करो" : "Start with a chapter — every journey begins with a first step"}
            </div>
            <div onClick={onBack} style={{ display: "inline-block", background: C.primaryMid, color: "white", padding: "12px 24px", borderRadius: 14, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              {isHi ? "Explore शुरू करें →" : "Start exploring →"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
